import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { CheckCircle2, AlertTriangle, XCircle, Plane } from "lucide-react";
import api from "@/lib/api";

const TINT = { green: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", red: "bg-rose-50 text-rose-700" };
const LABEL = { green: "Ready to go", amber: "Action recommended", red: "Action required" };

export default function PublicReadiness() {
  const { token } = useParams();
  const [rd, setRd] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/readiness/${token}`).then((r) => setRd(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="flex min-h-screen items-center justify-center text-slate-400">This checklist link is invalid or has expired.</div>;
  if (!rd) return <div className="flex min-h-screen items-center justify-center text-slate-400">Loading checklist…</div>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="public-readiness">
        <Logo variant="lg" />
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-slate-500"><Plane className="h-4 w-4" /> Trip Readiness</div>
          <button onClick={() => window.print()} className="no-print rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:border-indigo-300">Print / Save PDF</button>
        </div>
        <h1 className="font-brand text-2xl font-bold text-slate-800">{rd.trip?.title}</h1>
        <div className={`mt-4 flex items-center gap-3 rounded-xl p-3 ${TINT[rd.status]}`}>
          <span className="text-2xl font-bold">{rd.score.ok}/{rd.score.total}</span>
          <div>
            <p className="font-semibold">{LABEL[rd.status]}</p>
            {typeof rd.countdown?.days === "number" && rd.countdown.days >= 0 && (
              <p className="text-xs opacity-80">{rd.countdown.days > 0 ? `${rd.countdown.days} day${rd.countdown.days !== 1 ? "s" : ""} to departure` : "Departs today 🎉"}</p>
            )}
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {rd.checks.map((c) => (
            <div key={c.key} className="flex items-start gap-2">
              {c.status === "ok" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : c.status === "warn" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />}
              <div><p className="text-sm font-medium text-slate-700">{c.label}</p><p className="text-xs text-slate-500">{c.detail}</p></div>
            </div>
          ))}
        </div>
        {(rd.linked?.bookings?.length > 0 || rd.linked?.documents?.length > 0) && (
          <div className="mt-4 rounded-xl border border-slate-100 p-3">
            {rd.linked?.bookings?.length > 0 && (
              <>
                <p className="text-xs font-semibold text-slate-700">Itinerary — linked to this trip</p>
                {rd.linked.bookings.map((b, i) => (
                  <div key={`b${i}`} className="mt-1 text-xs text-slate-700">
                    <span className="font-medium">🧳 {b.title}</span>
                    {(b.date || b.time || b.location) && (
                      <span className="text-slate-600">{" — "}{[b.date, b.time, b.location].filter(Boolean).join(" · ")}</span>
                    )}
                  </div>
                ))}
              </>
            )}
            {rd.linked?.documents?.length > 0 && (
              <>
                <p className={`text-xs font-semibold text-slate-700 ${rd.linked?.bookings?.length > 0 ? "mt-3" : ""}`}>Documents</p>
                {rd.linked.documents.map((d, i) => <p key={`d${i}`} className="mt-0.5 text-xs text-slate-600">📄 {d.title}{d.category ? ` · ${d.category}` : ""}</p>)}
              </>
            )}
          </div>
        )}
        <div className="mt-4 flex flex-col items-center no-print">
          <img alt="QR to open this checklist" className="rounded-lg border border-slate-100"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`} />
          <p className="mt-1 text-[11px] text-slate-400">Scan to open on a phone</p>
        </div>
        <p className="mt-5 text-center text-[11px] text-slate-400">Shared via Suffle Online — Shop. Discover. Suffle.</p>
      </motion.div>
    </div>
  );
}
